/**
 * TerminationIntakeDialog
 *
 * The one way to end a contract from the web: captures the end date, reason
 * and the staff "approve immediately" choice, then creates a
 * `contract_termination` workflow item via `POST /contract-terminations` and
 * routes to the Review & Confirm screen, which walks through invoices, exit
 * fees and allocations. Opened from the contracts list, the contract editor
 * and the child's contracts tab.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { addDays, format } from 'date-fns';
import { useLingui } from '@lingui/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BaseEditableForm } from '@/components/form/BaseEditableForm';
import type { FieldConfig, GroupConfig } from '@/components/form/types';
import { useInitiateTermination, useContractTerminationSettings } from '@/hooks/useContractTermination';
import type { InitiateTerminationRequest } from '@/types/contractTermination';
import { contract_terminations_InitiateTerminationRequest } from '@/api';
import { typeError } from '@/lib/zodTypeError';

interface TerminationIntakeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Provide exactly one of contractId / contractGroupId. */
  contractId?: string;
  contractGroupId?: string;
}

interface IntakeFormValues extends Record<string, unknown> {
  effective_end_date: Date;
  reason: string;
  approve_immediately: boolean;
}

export function TerminationIntakeDialog({
  open,
  onOpenChange,
  contractId,
  contractGroupId,
}: TerminationIntakeDialogProps) {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const initiate = useInitiateTermination();
  const { noticePeriodDays } = useContractTerminationSettings();

  const schema = useMemo(
    () =>
      z.object({
        effective_end_date: z.date({
          error: typeError({
            required: i18n._({
            id: 'termination.intake.endDateRequired',
            message: 'End date is required',
          }),
          }),
        }),
        reason: z
          .string()
          .max(500, i18n._({ id: 'termination.intake.reasonTooLong', message: 'Reason is too long' }))
          .optional()
          .default(''),
        approve_immediately: z.boolean().default(true),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.locale],
  );

  const fields: FieldConfig<IntakeFormValues>[] = useMemo(
    () => [
      {
        name: 'effective_end_date',
        label: i18n._({ id: 'termination.intake.endDate', message: 'End date' }),
        type: 'date',
        required: true,
        colSpan: 2,
      },
      {
        name: 'reason',
        label: i18n._({ id: 'termination.intake.reason', message: 'Reason' }),
        type: 'textarea',
        colSpan: 2,
        placeholder: i18n._({
          id: 'termination.intake.reasonPlaceholder',
          message: 'Why is this contract ending?',
        }),
      },
      {
        name: 'approve_immediately',
        label: i18n._({
          id: 'termination.intake.approveImmediately',
          message: 'Approve immediately',
        }),
        type: 'boolean',
        colSpan: 2,
        description: i18n._({
          id: 'termination.intake.approveImmediatelyHint',
          message: 'Skip the request step and go straight to Review & Confirm.',
        }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.locale],
  );

  const groups: GroupConfig<IntakeFormValues>[] = useMemo(
    () => [
      {
        title: i18n._({ id: 'termination.intake.title', message: 'End contract' }),
        fields: ['effective_end_date', 'reason', 'approve_immediately'],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.locale],
  );

  const defaultValues = useMemo<Partial<IntakeFormValues>>(
    () => ({
      effective_end_date: addDays(new Date(), noticePeriodDays),
      reason: '',
      approve_immediately: true,
    }),
    [noticePeriodDays],
  );

  const handleSubmit = async (values: IntakeFormValues) => {
    const request: InitiateTerminationRequest = {
      contract_id: contractId,
      contract_group_id: contractId ? undefined : contractGroupId,
      requested_termination_date: format(values.effective_end_date, 'yyyy-MM-dd'),
      reason: values.reason ?? '',
      source: contract_terminations_InitiateTerminationRequest.source.WEB_ADMIN,
      auto_approve: values.approve_immediately,
    };

    const item = await initiate.mutateAsync(request);
    onOpenChange(false);
    if (item.id) {
      navigate(`/contract-terminations/${encodeURIComponent(item.id)}/review`);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !initiate.isPending) onOpenChange(false);
      }}
    >
      <DialogContent className="gap-0 p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle>
            {i18n._({ id: 'termination.intake.title', message: 'End contract' })}
          </DialogTitle>
          <DialogDescription>
            {i18n._({
              id: 'termination.intake.description',
              message:
                'Start the guided termination workflow. You will review affected invoices, fees, and allocations before anything changes.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-hidden flex flex-col">
          <BaseEditableForm<IntakeFormValues>
            schema={schema as unknown as z.ZodType<IntakeFormValues, IntakeFormValues>}
            fields={fields}
            groups={groups}
            defaultValues={defaultValues}
            mode="create"
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
            isSubmitting={initiate.isPending}
            inSheet
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
